/**
 * useWorkflowParamSources - Fontes de dados dos parâmetros das ações do Workflow
 *
 * O catálogo do banco (`workflow_action_types.params_contract`) declara, para
 * cada parâmetro, um `source` (ex.: `crm_sellers`, `crm_pipeline_phases`,
 * `tenant_members`, `kanban_categories`, `kanban_phases`, `flows`). Este hook
 * traduz esses códigos em listas de opções prontas para os seletores do
 * inspetor, REAPROVEITANDO os hooks já existentes de cada módulo — nada de
 * novas RPCs nem consultas duplicadas.
 *
 * @version 1.3.0
 * RELEASE: 2026-10-02 v1.3.0 - Entidade Veículo: novas fontes `vehicle_statuses`,
 *          `checklist_templates` e `distribution_queues` (reaproveita hooks existentes).
 * RELEASE: 2026-09-13 v1.2.0 - Recebe a entidade-raiz do processo e filtra as
 *          listas de tarefa (categorias, fases e motivos) pelo módulo
 *          correspondente (Tarefa | Retenção | Jurídico), impedindo que um
 *          processo de um módulo mova registros para outro.
 * RELEASE: 2026-09-12 v1.1.0 - Fases de tarefa passam a cobrir os TRÊS módulos
 *          (Tarefa/Retenção/Jurídico) com agrupamento "Módulo · Categoria", e
 *          nova fonte `task_completion_reasons` (motivos de conclusão de
 *          Retenção e Jurídico) para a ação de concluir do Workflow.
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (formulários guiados do designer)
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useCrmSellers } from '@/hooks/useCrmSellers';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { useTenantWorkspacesPipelines, type WsWithPipelines } from '@/hooks/useTenantWorkspacesPipelines';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
// v1.1.0: motivos de conclusão de Retenção/Jurídico (ação task.complete)
import { useTaskCompletionReasons } from '@/hooks/useTaskCompletionReasons';
// v1.2.0: tradução entidade-raiz -> tipo de tarefa (separação dos módulos)
import { taskTypeDaEntidade } from '@/components/workflow/taskEntity';
// v1.3.0: fontes do módulo Veículos/Checklist (hooks já existentes)
import { useVehicleStatuses } from '@/hooks/useVehicleStatuses';
import { useChecklistTemplatesList } from '@/hooks/useChecklistTemplates';
import { useGlobalDistributionQueues } from '@/hooks/useChecklistTypes';

const logger = createLogger('useWorkflowParamSources');

/** Opção exibida em um seletor guiado */
export interface WorkflowParamOption {
  value: string;
  label: string;
  /** Agrupamento opcional (ex.: workspace/pipeline, categoria da tarefa) */
  grupo?: string;
}

export interface WorkflowParamSources {
  crm_sellers: WorkflowParamOption[];
  crm_pipeline_phases: WorkflowParamOption[];
  tenant_members: WorkflowParamOption[];
  kanban_categories: WorkflowParamOption[];
  kanban_phases: WorkflowParamOption[];
  flows: WorkflowParamOption[];
  /** v1.1.0: árvore workspace → pipeline → fase (seleção em cascata do inspetor) */
  crm_hierarquia: WsWithPipelines[];
  /** v1.1.0: motivos de conclusão de Retenção/Jurídico (ação "Concluir tarefa") */
  task_completion_reasons: WorkflowParamOption[];
  /** v1.3.0: status de veículo, modelos de checklist e filas globais */
  vehicle_statuses: WorkflowParamOption[];
  checklist_templates: WorkflowParamOption[];
  distribution_queues: WorkflowParamOption[];
  isLoading: boolean;
}


/**
 * Retorna todas as listas de opções usadas pelos formulários guiados.
 *
 * @param entidade v1.2.0 — entidade-raiz do processo. Quando for de um módulo
 *        de tarefas (task | retention | legal), as listas de tarefa passam a
 *        mostrar somente os dados daquele módulo.
 */
export function useWorkflowParamSources(entidade?: string | null): WorkflowParamSources {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // v1.2.0: tipo de tarefa do processo (null quando não é módulo de tarefas)
  const tipoTarefa = taskTypeDaEntidade(entidade);

  const { activeSellers } = useCrmSellers();
  const { data: membros = [], isLoading: carregandoMembros } = useTenantMembers();
  const { data: workspaces = [], isLoading: carregandoPipelines } = useTenantWorkspacesPipelines();
  // v1.2.0: categorias do módulo do processo (padrão 'normal' fora de tarefas)
  const { categories, isLoading: carregandoCategorias } = useTaskCategories(true, tipoTarefa ?? 'normal');
  // v1.1.0: fases dos TRÊS módulos de tarefa (o hook sem override filtra pelo
  // módulo da rota — no designer de Workflow isso deixaria Retenção/Jurídico
  // sem fases para escolher)
  const { phases: fasesNormal = [], isLoading: carFasesN } = useTaskPhases(undefined, 'normal');
  const { phases: fasesRetencao = [], isLoading: carFasesR } = useTaskPhases(undefined, 'retention');
  const { phases: fasesJuridico = [], isLoading: carFasesJ } = useTaskPhases(undefined, 'legal');
  const carregandoFases = carFasesN || carFasesR || carFasesJ;
  // v1.1.0: motivos de conclusão (ação "Concluir tarefa" do Workflow)
  const { reasons: motivosRetencao = [] } = useTaskCompletionReasons('retention', true);
  const { reasons: motivosJuridico = [] } = useTaskCompletionReasons('legal', true);

  // v1.3.0: listas do módulo Veículos (só carregam em processos de Veículo/Negócio)
  const usaVeiculo = entidade === 'vehicle' || entidade === 'crm_deal';
  const { activeStatuses: statusVeiculo = [] } = useVehicleStatuses();
  const { data: modelosChecklist = [] } = useChecklistTemplatesList({ status: 'all' });
  const { data: filasGlobais = [] } = useGlobalDistributionQueues(usaVeiculo);

  // Fluxos publicados do tenant (usados pela ação "Disparar fluxo")
  const { data: fluxos = [], isLoading: carregandoFluxos } = useQuery({
    queryKey: ['workflow-param-flows', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<WorkflowParamOption[]> => {
      // v1.1.0: a tabela `flows` NÃO possui coluna `is_active` (o correto é
      // `status`). Selecionar coluna inexistente fazia o PostgREST devolver erro
      // e a lista de fluxos aparecia sempre vazia no designer.
      // v1.2.0 (2026-09-06): apenas fluxos publicados. O motor de fluxos recusa
      // executar rascunhos, então oferecê-los na lista gerava ação que nunca roda.
      const { data, error } = await supabase
        .from('flows')
        .select('id, name, status')
        .eq('tenant_id', tenantId!)
        .eq('status', 'active')
        .order('name');

      if (error) {
        logger.error('Falha ao listar fluxos para o designer', { error: error.message });
        throw error;
      }

      return (data || []).map((f: any) => ({
        value: f.id,
        label: f.name,
      }));

    },
  });


  const crm_pipeline_phases = useMemo<WorkflowParamOption[]>(
    () =>
      workspaces.flatMap((ws) =>
        ws.pipelines.flatMap((pipe) =>
          pipe.phases.map((fase) => ({
            value: fase.id,
            label: fase.name,
            grupo: `${ws.name} · ${pipe.name}`,
          })),
        ),
      ),
    [workspaces],
  );

  return {
    crm_sellers: (activeSellers ?? []).map((s: any) => ({ value: s.id, label: s.name })),
    crm_pipeline_phases,
    tenant_members: membros.map((m) => ({ value: m.user_id, label: m.full_name || m.email || 'Usuário' })),
    kanban_categories: (categories ?? []).map((c: any) => ({ value: c.id, label: c.name })),
    // v1.2.0: fases apenas do módulo do processo (quando é módulo de tarefas)
    kanban_phases: [
      ...(!tipoTarefa || tipoTarefa === 'normal'
        ? fasesNormal.map((p: any) => ({ value: p.id, label: p.name, grupo: `Tarefas · ${p.category_name ?? ''}` }))
        : []),
      ...(!tipoTarefa || tipoTarefa === 'retention'
        ? fasesRetencao.map((p: any) => ({ value: p.id, label: p.name, grupo: `Retenção · ${p.category_name ?? ''}` }))
        : []),
      ...(!tipoTarefa || tipoTarefa === 'legal'
        ? fasesJuridico.map((p: any) => ({ value: p.id, label: p.name, grupo: `Jurídico · ${p.category_name ?? ''}` }))
        : []),
    ],
    flows: fluxos,
    crm_hierarquia: workspaces,
    // v1.2.0: motivos de conclusão apenas do módulo do processo
    task_completion_reasons: [
      ...(!tipoTarefa || tipoTarefa === 'retention'
        ? motivosRetencao.map((m: any) => ({ value: m.id, label: m.name, grupo: 'Retenção' }))
        : []),
      ...(!tipoTarefa || tipoTarefa === 'legal'
        ? motivosJuridico.map((m: any) => ({ value: m.id, label: m.name, grupo: 'Jurídico' }))
        : []),
    ],
    vehicle_statuses: statusVeiculo.map((st: any) => ({ value: st.id, label: st.label })),
    checklist_templates: (modelosChecklist as any[]).map((m) => ({ value: m.id, label: m.name, grupo: m.type_name ?? undefined })),
    distribution_queues: (filasGlobais as any[]).map((q) => ({ value: q.id, label: q.name })),
    isLoading:
      carregandoMembros || carregandoPipelines || carregandoCategorias || carregandoFases || carregandoFluxos,
  };
}
